import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {any} */
  #productJson = null;

  /** @type {Map<string, string | null>} */
  #htmlCache = new Map();

  /** @type {number | null} Track current slideshow position to maintain on size-only changes */
  #currentSlideIndex = null;

  /** @type {any} Track the last selected variant's options to detect what changed */
  #lastSelectedVariantOptions = null;

  /** @type {number | null} index of the color option in variant.options */
  #colorOptionIndex = null;

  /** @type {Record<string, number>} keeps last position per color */
  #positionByColor = {};
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    // Track slideshow position changes to maintain when user navigates
    document.addEventListener('slideshow:select', (event) => {
      /** @type {any} */
      const detail = event.detail;
      if (detail?.index !== undefined) {
        this.#currentSlideIndex = detail.index;

        const colorIndex = this.getColorOptionIndex();
        const currentColor = this.#lastSelectedVariantOptions?.[colorIndex];
        if (currentColor) {
          this.#positionByColor[currentColor] = detail.index;
        }
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('pointerenter', this.prefetchVariant.bind(this), { capture: true });
    this.#resizeObserver.observe(this);

    // Fetch the JSON payload once dynamically to enable pure local swaps
    const productUrl = this.dataset.productUrl?.split('?')[0] || window.location.pathname;
    fetch(productUrl + '.js')
      .then(res => res.json())
      .then(json => { 
        this.#productJson = json; 

        // Important: If product.js only returns images but we have mixed media, 
        // we need to ensure the positions match the DOM.
        
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        const selectedOption = /** @type {HTMLElement | null} */ (this.querySelector('input:checked, option[selected]'));
        const variantId = urlVariantId || selectedOption?.getAttribute('data-variant-id') || (json.variants && json.variants[0]?.id);
        
        if (variantId) {
          const url = new URL(window.location.href);
          // Use a staggered approach to ensure the DOM is ready for filtering
          const runInitFilter = () => this.applyLocally(variantId, url);
          
          if (document.readyState === 'complete') {
            // For dynamic entries like the expander, DOM is already "complete", 
            // so we use a microtask/animation frame to ensure morphing finish.
            requestAnimationFrame(() => setTimeout(runInitFilter, 10));
          } else {
            window.addEventListener('load', () => setTimeout(runInitFilter, 100));
          }
        }
      })
      .catch(() => {}); // Catch silent loading errors
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Resolve color option index in variant options array.
   * @returns {number}
   */
  getColorOptionIndex() {
    if (this.#colorOptionIndex !== null) {
      return this.#colorOptionIndex;
    }

    if (!this.#productJson || !Array.isArray(this.#productJson.options)) {
      this.#colorOptionIndex = 0;
      return 0;
    }

    const lowerOptions = this.#productJson.options.map((o) => String(o || '').toLowerCase());
    const colorIndex = lowerOptions.findIndex((name) => 
      name.includes('color') || 
      name.includes('顏色') || 
      name.includes('颜色')
    );
    this.#colorOptionIndex = colorIndex >= 0 ? colorIndex : 0;
    return this.#colorOptionIndex;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const url = new URL(window.location.href);
    let variantId = selectedOption.dataset.variantId || null;

    // Resolve variant ID dynamically to ensure it's not stale from a previous client-side swap
    if (this.#productJson && this.#productJson.variants) {
      const selectedInputs = Array.from(this.querySelectorAll('input:checked, select'));
      const selectedValues = selectedInputs.map((el) => /** @type {any} */ (el).value).filter(Boolean);
      const matchedVariant = this.#productJson.variants.find((v) => {
        const options = [v.option1, v.option2, v.option3].filter(Boolean);
        return options.length === selectedValues.length && options.every((opt) => selectedValues.includes(opt));
      });
      if (matchedVariant) variantId = String(matchedVariant.id);
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    // Pure Client-Side Swap Override (No Fetch)
    if (this.#productJson && variantId && !loadsNewProduct) {
      this.applyLocally(variantId, url);
      return;
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @param {any} variant
   * @returns {number[] | null}
   */
  getVariantMediaIndices(variant) {
    const allMedia = this.#productJson.media || this.#productJson.images;
    if (!allMedia || allMedia.length === 0) return null;

    /**
     * Helper to find the first image at or after a position.
     * @param {number} startPos
     * @returns {number}
     */
    const findImageBoundary = (startPos) => {
      for (let i = startPos; i < allMedia.length; i++) {
        const item = allMedia[i];
        const type = (item && typeof item === 'object') ? (item.media_type || item.type) : 'image';
        if (!type || type === 'image') return i;
      }
      return startPos;
    };

    let startIndex = 0;
    if (variant.featured_image && variant.featured_image.position) {
      startIndex = findImageBoundary(variant.featured_image.position - 1);
    }

    let endIndex = allMedia.length;
    
    // Find all unique image-based boundaries from all variants
    const allPositions = [...new Set(this.#productJson.variants
      .map((v) => {
        if (!v.featured_image || !v.featured_image.position) return null;
        return findImageBoundary(v.featured_image.position - 1);
      })
      .filter((p) => p !== null))]
      .sort((a,b) => a - b);
      
    const nextPos = allPositions.find((p) => p > startIndex);
    if (nextPos !== undefined) {
      endIndex = nextPos;
    }

    const allowedIndices = [];
    for (let i = startIndex; i < endIndex; i++) {
      allowedIndices.push(i);
    }

    // Inclusion Logic: Capture any videos immediately preceding this group's start image
    let searchIdx = startIndex - 1;
    while (searchIdx >= 0) {
      const item = allMedia[searchIdx];
      const type = (item && typeof item === 'object') ? (item.media_type || item.type) : 'image';
      if (type && type !== 'image') {
        allowedIndices.unshift(searchIdx);
        searchIdx--;
      } else {
        break;
      }
    }

    return allowedIndices;
  }

  /**
   * @param {string | number} variantId
   * @param {URL} url
   */
  applyLocally(variantId, url) {
    const variant = this.#productJson.variants.find(/** @param {any} v */ (v) => v.id == variantId);
    if (!variant) return;

    // 1. SKU and Barcode updates are handled automatically by ProductSkuComponent listening for VariantUpdateEvent

    // 2. Update Add To Cart / Product Form explicitly
    document.querySelectorAll(`product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]`).forEach(ele => {
      /** @type {HTMLInputElement} */
      const input = (ele);
      const form = input.closest('form');
      if (form) {
        const isSameProduct = form.closest(`product-information`)?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`) 
          || form.closest(`product-card[data-product-id="${this.dataset.productId}"]`)
          || true; // Fallback update all for pure swap

        if (isSameProduct) {
          input.value = variant.id;
        }
      }
    });

    const addToCartContainer = this.closest('.shopify-section, product-card')?.querySelector('add-to-cart-component');
    if (addToCartContainer) {
      const btn = addToCartContainer.querySelector('button');
      const span = btn?.querySelector('.add-to-cart-text');
      if (variant.available) {
        btn?.removeAttribute('disabled');
        if (span) span.textContent = 'Add to cart';
      } else {
        btn?.setAttribute('disabled', 'disabled');
        if (span) span.textContent = 'Sold out';
      }
    }

    // 3. Update Gallery Images silently
    const mediaGalleries = document.querySelectorAll(`media-gallery`);
    mediaGalleries.forEach((gallery) => {
      const productInfo = gallery.closest('.product-information, .shopify-section, product-card') || gallery.parentElement;
      if (productInfo) {
        // Save current slideshow position before updating (only once, not per render)
        const slideshow = gallery.querySelector('slideshow-component, .slideshow');
        if (slideshow && this.#currentSlideIndex === null) {
          const currentAttr = slideshow.getAttribute('initial-slide');
          this.#currentSlideIndex = currentAttr ? parseInt(currentAttr, 10) : 0;
        }

        const allowedIndices = this.getVariantMediaIndices(variant);
        if (!allowedIndices) return;

        const colorIndex = this.getColorOptionIndex();
        const currentColor = variant.options?.[colorIndex];
        const previousColor = this.#lastSelectedVariantOptions?.[colorIndex];
        const isSameColor = currentColor && previousColor && currentColor === previousColor;

        let nextIndex = allowedIndices[0];

        if (isSameColor && currentColor && typeof this.#positionByColor[currentColor] === 'number') {
          const savedIndex = this.#positionByColor[currentColor];
          if (allowedIndices.includes(savedIndex)) {
            nextIndex = savedIndex;
          }
        } else if (variant.featured_image && variant.featured_image.position) {
          const featuredIndex = variant.featured_image.position - 1;
          if (allowedIndices.includes(featuredIndex)) {
            nextIndex = featuredIndex;
          }
        }

        const slides = gallery.querySelectorAll('slideshow-slide, .media-gallery__grid li');
        slides.forEach((ele, idx) => {
          /** @type {HTMLElement} */
          const slide = (ele);
          if (allowedIndices.includes(idx)) {
            slide.removeAttribute('hidden');
            slide.setAttribute('reveal', '');
            slide.style.display = ''; 
          } else {
            slide.setAttribute('hidden', '');
            slide.removeAttribute('reveal');
            slide.style.display = 'none';
          }
        });
        
        const thumbnails = gallery.querySelectorAll('.slideshow-controls__thumbnail');
        thumbnails.forEach((ele, idx) => {
          /** @type {HTMLElement} */
          const thumb = (ele);
          if (allowedIndices.includes(idx)) {
            thumb.removeAttribute('hidden');
            thumb.style.display = '';
          } else {
            thumb.setAttribute('hidden', '');
            thumb.style.display = 'none';
          }
        });
        
        /** @type {any} */
        const slideshowAny = slideshow;
        if (slideshowAny && slideshowAny.select && allowedIndices.length > 0) {
          // Use relative index within the visible slides
          const relativeIndex = allowedIndices.indexOf(nextIndex);
          if (relativeIndex !== -1) {
            slideshowAny.select(relativeIndex, undefined, { animate: false });
          }
          if (currentColor) {
            this.#positionByColor[currentColor] = nextIndex;
          }
        }

        // Update the last selected variant options for next comparison
        this.#lastSelectedVariantOptions = variant.options;      }
    });

    // 4. Fire standard update events locally manually so other listeners don't break
    this.dispatchEvent(
      new VariantUpdateEvent(variant, this.selectedOptionId || '', {
        html: document, // Fake doc structure to avoid null HTML morph errors in listeners
        productId: this.dataset.productId ?? '',
      })
    );

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Prefetches the variant HTML.
   * @param {Event} event - The pointerenter event.
   */
  prefetchVariant(event) {
    /** @type {any} */
    const target = event.target;
    if (!target || typeof target.matches !== 'function' || !target.matches('label[for], option')) return;

    /** @type {any} */
    let targetInput = null;
    /** @type {any} */
    let currentlyCheckedInput = null;
    /** @type {HTMLSelectElement | null} */
    let parentSelect = null;

    if (target.tagName === 'LABEL') {
      const inputId = target.getAttribute('for');
      targetInput = inputId ? this.querySelector(`#${inputId}`) : null;
      if (!targetInput || targetInput.checked) return;
      
      const fieldset = targetInput.closest('fieldset');
      if (fieldset) currentlyCheckedInput = fieldset.querySelector('input:checked');
    } else {
      targetInput = target;
      parentSelect = targetInput.closest('select');
      if (!parentSelect || targetInput.hasAttribute('selected')) return;
    }

    if (!targetInput) return;

    // Temporarily apply state to build correct URL
    if (currentlyCheckedInput && targetInput.tagName !== 'OPTION') {
      currentlyCheckedInput.checked = false;
      targetInput.checked = true;
    } else if (parentSelect) {
      const oldSelected = parentSelect.querySelector('option[selected]');
      if (oldSelected) {
        oldSelected.removeAttribute('selected');
        currentlyCheckedInput = oldSelected;
      }
      targetInput.setAttribute('selected', '');
    }

    const requestUrl = this.buildRequestUrl(targetInput);

    // Restore state
    if (currentlyCheckedInput && currentlyCheckedInput.tagName !== 'OPTION') {
      targetInput.checked = false;
      currentlyCheckedInput.checked = true;
    } else if (parentSelect) {
      targetInput.removeAttribute('selected');
      if (currentlyCheckedInput) currentlyCheckedInput.setAttribute('selected', '');
    }

    if (!requestUrl || this.#htmlCache.has(requestUrl)) return;

    this.#htmlCache.set(requestUrl, null); // mark pending
    fetch(requestUrl)
      .then((response) => response.text())
      .then((text) => this.#htmlCache.set(requestUrl, text))
      .catch(() => this.#htmlCache.delete(requestUrl));
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    /**
     * @param {string} responseText
     */
    const processHtml = (responseText) => {
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
      if (!textContent) return;

      let newProduct;
      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        newProduct = this.updateVariantPicker(html);
      }

      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
          })
        );
      }
    };

    const cachedText = this.#htmlCache.get(requestUrl);
    if (typeof cachedText === 'string') {
      yieldToMainThread().then(() => processHtml(cachedText));
      return;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#htmlCache.set(requestUrl, responseText);
        processHtml(responseText);
      })
      .catch((error) => {
        // Silent catch for fetch errors
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
